import React from 'react';
import { PercentileBar } from './graphics';
import { fmtValue, longDate } from './format';

const BASIS_LABEL = { sac: 'sub asset class', style: 'style within SAC', tilt: 'tilt within SAC' };

// Scope dropdown for the held-manager view: models, any portfolio, then registry suites.
export function ScopeSelect({ scopes, scope, setScope }) {
  const suites = (scopes || []).filter((s) => typeof s.scope === 'string' && s.scope.startsWith('suite:'));
  const plain = (scopes || []).filter((s) => typeof s.scope === 'string' && !s.scope.startsWith('suite:'));
  return (
    <select value={scope} onChange={(e) => setScope(e.target.value)} title="Which portfolios count as 'holding' a manager">
      {plain.length === 0 && <option value="models">Drummond models (registry, non-tailored)</option>}
      {plain.map((s) => <option key={s.scope} value={s.scope}>{s.label} ({s.n_portfolios})</option>)}
      {suites.some((s) => !s.tailored) && (
        <optgroup label="Drummond series">
          {suites.filter((s) => !s.tailored).map((s) => <option key={s.scope} value={s.scope}>{s.label} ({s.n_portfolios})</option>)}
        </optgroup>
      )}
      {suites.some((s) => s.tailored) && (
        <optgroup label="Tailored series">
          {suites.filter((s) => s.tailored).map((s) => <option key={s.scope} value={s.scope}>{s.label} ({s.n_portfolios})</option>)}
        </optgroup>
      )}
    </select>
  );
}

// Percentile figures for one row on the chosen basis, with the API's SAC figures as
// the fallback so older payloads still render.
const sinceOn = (r, basis) => (r.since?.by?.[basis]) || (r.since ? { pct_return: r.since.pct_return, pct_active: r.since.pct_active, cohort_n: r.since.cohort_n, thin: r.since.thin } : null);
const currentOn = (r, p, basis) => (r.current?.[p]?.by?.[basis]) || { percentile: r.current?.[p]?.percentile, cohort_n: 0, thin: false };
const allBases = (obj, key) => ['sac', 'style', 'tilt'].map((b) => `${BASIS_LABEL[b]}: ${obj?.[b]?.[key] === null || obj?.[b]?.[key] === undefined ? '–' : Math.round(obj[b][key])}${obj?.[b]?.cohort_n ? ` of ${obj[b].cohort_n}` : ''}`).join('\n');

// Presentational part of "Our manager selection": tiles, by-sleeve chips, the grouped table.
export default function HeldTable({ data, basis = 'sac', seriesFor, onShowSeries }) {
  if (!data) return null;
  const hasScore = !!data.weighting;
  const bySac = data.summary?.by_sub_asset_class || {};
  const apMissing = data.summary?.ap_held_not_in_scope || [];
  const sum = (data.summary?.by_basis?.[basis]) || { n_with_since: data.summary?.n_with_since, share_above_median: data.summary?.share_above_median, avg_percentile: data.summary?.avg_percentile };
  const rows = (data.rows || []).map((r, i, all) => ({ r, header: i === 0 || all[i - 1].sub_asset_class !== r.sub_asset_class }));
  const basisLabel = BASIS_LABEL[basis] || basis;
  return (
    <>
      <div className="qs-tiles">
        <div className="qs-stat"><div className="qs-stat-num">{data.summary.n_held}</div><div className="qs-stat-label">managers held now</div></div>
        <div className="qs-stat"><div className="qs-stat-num">{sum.share_above_median === null || sum.share_above_median === undefined ? '–' : `${Math.round(sum.share_above_median * 100)}%`}</div><div className="qs-stat-label">above median of their {basisLabel} since held ({sum.n_with_since} measurable)</div></div>
        <div className="qs-stat"><div className="qs-stat-num">{sum.avg_percentile === null || sum.avg_percentile === undefined ? '–' : sum.avg_percentile.toFixed(0)}</div><div className="qs-stat-label">average since-held percentile in {basisLabel}</div></div>
        <div className="qs-stat"><div className="qs-stat-num">{data.summary.n_censored}</div><div className="qs-stat-label">held since before the history starts</div></div>
        {data.include_exited && <div className="qs-stat"><div className="qs-stat-num">{data.summary.n_exited ?? 0}</div><div className="qs-stat-label">exited managers listed (not in the figures above)</div></div>}
      </div>
      {basis === 'sac' && Object.keys(bySac).length > 0 && (
        <div className="qs-chips">
          {Object.entries(bySac).map(([k, v]) => (
            <span key={k} className="qs-chip" title={`${v.n} funds; average percentile ${v.avg_percentile}`}>
              {k}: {v.share_above_median === null || v.share_above_median === undefined ? '–' : `${Math.round(v.share_above_median * 100)}%`} above median <span className="qs-count">({v.n})</span>
            </span>
          ))}
        </div>
      )}
      <div className="qs-grid-scroll" style={{ maxHeight: '62vh' }}>
        <table className="qs-grid">
          <thead>
            <tr>
              <th className="qs-l">Fund</th>
              <th className="qs-l">Style · tilt</th>
              <th className="qs-l">Held since</th>
              <th>Months</th>
              <th>Ports</th>
              <th>Fund p.a.</th>
              <th>Bench p.a.</th>
              <th>Excess</th>
              <th className="qs-l" title={`Percentile of the since-held return within the fund's ${basisLabel}`}>Pct of return since held</th>
              <th className="qs-l" title={`Percentile of the since-held active return within the fund's ${basisLabel}`}>Pct of active since held</th>
              <th className="qs-l" title={`Percentile of the chosen statistic over 1y within the fund's ${basisLabel}`}>Pct 1y now</th>
              <th className="qs-l" title={`Percentile of the chosen statistic over 3y within the fund's ${basisLabel}`}>Pct 3y now</th>
              {hasScore && <th>Score</th>}
              {hasScore && <th>SAC rank</th>}
              <th className="qs-l" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ r, header }) => {
              const ports = (r.portfolios || []).map((p) => `${p.code} from ${p.first}${p.current ? '' : ' (exited)'}`).join('; ');
              const s = sinceOn(r, basis);
              const c1 = currentOn(r, '1y', basis);
              const c3 = currentOn(r, '3y', basis);
              return (
                <React.Fragment key={r.tr_code}>
                  {header && <tr className="qs-sac-row"><td colSpan={13 + (hasScore ? 2 : 0)}>{r.sub_asset_class || 'Unclassified'}</td></tr>}
                  <tr className={`${seriesFor === r.tr_code ? 'qs-row-sel' : ''}${r.exited ? ' qs-row-exited' : ''}`}>
                    <td className="qs-l qs-name" title={`${r.tr_code}${r.apir ? ' · ' + r.apir : ''}\nBenchmark: ${r.benchmark_name || '–'}\nPortfolios: ${ports}`}>
                      {r.name || r.tr_code}
                      {r.exited && <span className="qs-tag qs-tag-exited" title={`No longer in any in-scope portfolio; last period ${r.exited_on}`}>exited {r.exited_on ? r.exited_on.slice(0, 7) : ''}</span>}
                      {!r.ap_held && !r.exited && <span className="qs-tag qs-tag-stale" title="Held in Shadow_Data but AP List Held = 0">AP List: not held</span>}
                    </td>
                    <td className="qs-l qs-dim">{r.style || '–'} · {r.tilt || '–'}</td>
                    <td className="qs-l qs-dim">{r.since_held_label}{r.censored && <span className="qs-tag" title={`Already held when the fund-level history starts (${data.holdings_start})`}>censored</span>}</td>
                    <td className="qs-num qs-dim" title={r.coverage !== null && r.coverage !== undefined ? `${r.observed} observed, coverage ${(r.coverage * 100).toFixed(0)}%` : ''}>{r.months_held}</td>
                    <td className="qs-num qs-dim">{r.n_portfolios}</td>
                    {r.since ? (
                      <>
                        <td className="qs-num">{fmtValue(r.since.fund, 'pct')}</td>
                        <td className="qs-num qs-dim">{fmtValue(r.since.benchmark, 'pct')}</td>
                        <td className={`qs-num ${r.since.excess > 0 ? 'qs-pos' : r.since.excess < 0 ? 'qs-neg' : ''}`}>{fmtValue(r.since.excess, 'pct')}</td>
                        <td className="qs-l" title={allBases(r.since.by, 'pct_return')}><PercentileBar pct={s?.pct_return} thin={!!s?.thin} n={s?.cohort_n || 0} /></td>
                        <td className="qs-l" title={allBases(r.since.by, 'pct_active')}><PercentileBar pct={s?.pct_active} thin={!!s?.thin} n={s?.cohort_n || 0} /></td>
                      </>
                    ) : (
                      <td colSpan={5} className="qs-null">{r.warning || 'no since-held figures'}</td>
                    )}
                    <td className="qs-l" title={allBases(r.current?.['1y']?.by, 'percentile')}><PercentileBar pct={c1.percentile} thin={!!c1.thin} n={c1.cohort_n || 0} /></td>
                    <td className="qs-l" title={allBases(r.current?.['3y']?.by, 'percentile')}><PercentileBar pct={c3.percentile} thin={!!c3.thin} n={c3.cohort_n || 0} /></td>
                    {hasScore && <td className="qs-num">{r.score?.total === null || r.score?.total === undefined ? '–' : r.score.total.toFixed(1)}</td>}
                    {hasScore && <td className="qs-num qs-dim">{r.score?.rank_in_cohort ? `${r.score.rank_in_cohort}/${r.score.cohort_scored}` : '–'}</td>}
                    <td className="qs-l qs-actions">{r.since && <button type="button" className="qs-link" onClick={() => onShowSeries(r.tr_code)}>chart</button>}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="qs-foot">
        Since-held = first Shadow_Data period with a positive target in an in-scope portfolio, snapped to that month-end; returns run from the following month to the as-at month.
        Percentiles since held rank the fund against members of its {basisLabel} with full coverage of the same window (100 = best); hover a bar to see all three bases. Style and tilt peer groups are small, so the "thin" marker appears below 8 funds.
        {data.include_exited ? ' Exited managers are measured from purchase to their last in-scope period and are listed after current holdings in each sleeve; the summary figures exclude them.' : ' Current holdings only; tick "include exited managers" to list sold managers over their holding period.'}
        &quot;At least&quot; = already held when the fund-level history starts ({longDate(data.holdings_start)}). Universe is current funds (survivorship). The holding date is the model shadow book, not every client&apos;s purchase date.
        {apMissing.length > 0 && ` AP List marks ${apMissing.length} further funds as held that are not in this scope: ${apMissing.slice(0, 12).join(', ')}${apMissing.length > 12 ? '…' : ''}.`}
      </div>
    </>
  );
}
